import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@ohpen/enso-ui';

import { ConfigTableActions, LoadFailed } from '@config/components';
import { useConfigContext } from '@config/context';
import type { ValueListKind, ValueListMap } from '@config/interfaces';
import { CONFIG_PAGINATION, CONFIG_SCROLL } from '@config/utils';
import { getStatusTagMappings, listColumns, STATUS_LIST_NAME, valueColumns } from '../tableConfig';
import { toRows } from '../utils';

/** One kind of list, fetched through the host. */
export const ValueListTable = ({ kind, active }: { kind: ValueListKind; active: boolean }): ReactElement => {
  const { t } = useTranslation();
  const { getValueLists } = useConfigContext();
  const [lists, setLists] = useState<ValueListMap | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  // Fetches once the tab is shown for the first time, so opening the page reads only the fixed lists.
  useEffect(() => {
    if (!active || !getValueLists || lists) return;

    let cancelled = false;
    setIsLoading(true);
    setHasFailed(false);
    getValueLists(kind)
      .then(result => {
        if (!cancelled) setLists(result);
      })
      .catch(() => {
        if (!cancelled) setHasFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [active, getValueLists, kind, lists]);

  const statusList = useMemo(
    () => ({
      active: t('config.valueLists.status.active'),
      disabled: t('config.valueLists.status.disabled'),
      hidden: t('config.valueLists.status.hidden'),
    }),
    [t],
  );

  const dataSource = useMemo(() => toRows(lists, statusList), [lists, statusList]);

  const valueLists = useMemo(() => ({ [STATUS_LIST_NAME]: statusList }), [statusList]);

  if (hasFailed) return <LoadFailed />;

  return (
    <Table
      id={`configValueLists-${kind}`}
      rowKey="id"
      dataSource={dataSource}
      columns={listColumns}
      valueLists={valueLists}
      tagMappings={getStatusTagMappings()}
      expanded={{ rowKey: 'code', columnsKey: 'values', columns: valueColumns }}
      loading={isLoading}
      pagination={CONFIG_PAGINATION}
      scroll={CONFIG_SCROLL}
      itemsLabel={t(`config.valueLists.tabs.${kind}`)}
    >
      <ConfigTableActions />
    </Table>
  );
};
